import { useEffect, useState } from 'react'
import { useMeProfile } from '@/features/profile/api/queries'
import { useUpdateCookieConsent } from '@/features/profile/api/mutations'
import {
  cookieConsentFromProfile,
  dispatchCookieConsentChanged,
  type CookieConsent,
} from '@/shared/lib/cookieConsent'

const CONSENT_CHANGED = 'cse:cookie-consent-changed'

export type ConsentChoice = Pick<CookieConsent, 'analytics' | 'functional' | 'marketing'>

/**
 * Live cookie-consent snapshot (AV-X03). Source of truth is `GET /me` (`cookie_consent` in DB).
 */
export function useCookieConsent(): CookieConsent | null {
  const profile = useMeProfile()
  const profileConsent = cookieConsentFromProfile(profile.data)
  const [eventConsent, setEventConsent] = useState<CookieConsent | null>(null)

  useEffect(() => {
    const onChanged = (event: Event): void => {
      const detail = (event as CustomEvent<CookieConsent>).detail
      if (detail?.version === 1) {
        setEventConsent(detail)
      }
    }

    window.addEventListener(CONSENT_CHANGED, onChanged)
    return () => window.removeEventListener(CONSENT_CHANGED, onChanged)
  }, [])

  return eventConsent ?? profileConsent
}

export function usePersistCookieConsent() {
  const mutation = useUpdateCookieConsent()

  return async (next: ConsentChoice): Promise<CookieConsent> => {
    const profile = await mutation.mutateAsync(next)
    const consent = cookieConsentFromProfile(profile)
    if (consent == null) {
      throw new Error('Cookie consent missing after save')
    }
    dispatchCookieConsentChanged(consent)
    return consent
  }
}

export function isAnalyticsAllowed(consent: CookieConsent | null): boolean {
  return consent?.analytics === true
}

export function isMarketingAllowed(consent: CookieConsent | null): boolean {
  return consent?.marketing === true
}

export function isFunctionalAllowed(consent: CookieConsent | null): boolean {
  return consent?.functional === true
}
